import React, { useMemo } from 'react';
import { TodoItem } from '../lib/todos';

interface MonthViewProps {
    date: Date;
    items: TodoItem[];
    onDateChange: (next: Date) => void;
    onSelectDay: (date: Date) => void;
    onSelectEvent: (id: string) => void;
    onSwitchToDay?: (date: Date) => void; // optional: request parent to switch to Day view
}

function startOfMonth(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfWeek(d: Date) {
    const c = new Date(d);
    const day = c.getDay();
    const diff = c.getDate() - day;
    return new Date(c.setDate(diff));
}
function addDays(d: Date, days: number) {
    const c = new Date(d);
    c.setDate(c.getDate() + days);
    return c;
}
function ymdLocal(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function isSameDay(a: Date, b: Date) {
    return ymdLocal(a) === ymdLocal(b);
}

export default function MonthView({ date, items, onDateChange, onSelectDay, onSelectEvent, onSwitchToDay }: MonthViewProps) {
    const first = useMemo(() => startOfMonth(date), [date]);
    const last = useMemo(() => endOfMonth(date), [date]);
    const gridStart = useMemo(() => startOfWeek(first), [first]);
    const cells = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)), [gridStart]);

    // group items by day
    const eventsByDay = useMemo(() => {
        const map: Record<string, TodoItem[]> = {};
        cells.forEach(c => { map[ymdLocal(c)] = []; });
        items.forEach(it => {
            if (!it.due_date) return;
            const key = ymdLocal(new Date(it.due_date));
            if (!map[key]) map[key] = [];
            map[key].push(it);
        });
        // sort per day by time then title
        Object.values(map).forEach(list => list.sort((a, b) => (a.due_date || '').localeCompare(b.due_date || '') || (a.title || '').localeCompare(b.title || '')));
        return map;
    }, [items, cells]);

    const today = new Date();

    return (
        <div className="w-full bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 border-b border-gray-800">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="px-2 py-2 text-xs text-gray-400 text-center">{d}</div>
                ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7 divide-x divide-gray-800">
                {cells.map((d, idx) => {
                    const inMonth = d.getMonth() === date.getMonth();
                    const isTodayFlag = isSameDay(d, today);
                    const dayKey = ymdLocal(d);
                    const evs = eventsByDay[dayKey] || [];
                    return (
                        <div
                            key={idx}
                            className={`min-h-[120px] border-b border-gray-800 p-1 ${inMonth ? '' : 'bg-gray-900/40'}`}
                            onClick={() => { const next = new Date(d); onDateChange(next); onSelectDay(next); onSwitchToDay && onSwitchToDay(next); }}
                        >
                            <div className={`flex items-center justify-between mb-1`}>
                                <div className={`text-xs ${inMonth ? 'text-gray-300' : 'text-gray-500'}`}>
                                    <span className={`${isTodayFlag ? 'inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white' : ''}`}>
                                        {d.getDate()}
                                    </span>
                                </div>
                                {/* Today chip removed; we highlight the date bubble instead */}
                            </div>
                            <div className="space-y-1">
                                {evs.slice(0, 3).map(e => (
                                    <div key={e.id} className="px-1 py-0.5 rounded bg-blue-600 text-white text-[10px] truncate cursor-pointer hover:bg-blue-500" onClick={(ev) => { ev.stopPropagation(); onSelectEvent(e.id); }}>
                                        {e.title}
                                    </div>
                                ))}
                                {evs.length > 3 && (
                                    <div className="text-[10px] text-gray-400">+{evs.length - 3} more</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
